/**
 * Ted #25 / #30 (#986, #1109 AC3, lane LR) — the question Ted asked for, in
 * his words: "Change this reservation only" or "Change this and future
 * reservations?". Asked BEFORE the edit dialog opens on an occurrence of a
 * recurring series; the answer decides where the commit goes.
 */
import { useEffect, useRef } from 'react';
import type { SeriesEditScope } from '../../lib/api.js';
import './Recurring.css';

export interface RecurringEditScopeDialogProps {
  /** The dog(s) and date, never a uuid. */
  label: string;
  onChoose: (scope: SeriesEditScope) => void;
  onClose: () => void;
}

export function RecurringEditScopeDialog({ label, onChoose, onClose }: RecurringEditScopeDialogProps): JSX.Element {
  const firstRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="recurring-scope-title" className="ferro-blocking-dialog__overlay" data-testid="recurring-scope-dialog">
      <div className="ferro-blocking-dialog__panel">
        <h2 id="recurring-scope-title">{label} is part of a recurring series</h2>
        <p className="ferro-recurring__lede">What should this change apply to?</p>
        <div className="ferro-scope__choices">
          <button ref={firstRef} type="button" className="ferro-scope__choice" onClick={() => onChoose('this_only')} data-testid="recurring-scope-this-only">
            <strong>Change this reservation only</strong>
            <span>This date steps out of the series; the other dates stay as they are.</span>
          </button>
          <button type="button" className="ferro-scope__choice" onClick={() => onChoose('this_and_future')} data-testid="recurring-scope-this-and-future">
            <strong>Change this and future reservations</strong>
            <span>The same change is made to every later date in the series. Earlier dates are left alone.</span>
          </button>
        </div>
        <div className="ferro-blocking-dialog__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose} data-testid="recurring-scope-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
